"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useClubMachineFilter = useClubMachineFilter;
const react_1 = require("react");
const react_i18next_1 = require("react-i18next");
const ApiClubRepo_1 = require("../../modules/club/infra/ApiClubRepo");
const getAll_1 = require("../../modules/club/app/getAll");
const getBasicInfo_1 = require("../../modules/machine/app/getBasicInfo");
const ApiMachineRepo_1 = require("../../modules/machine/infra/ApiMachineRepo");
const useTableFilters_1 = require("./useTableFilters");
const FILTER_KEYS = ['clubs', 'machines'];
function useClubMachineFilter({ storageKey, onFilterChange, }) {
    const { t } = (0, react_i18next_1.useTranslation)();
    // --- data fetching (domain-specific) ---
    const [clubs, setClubs] = (0, react_1.useState)([]);
    const [machines, setMachines] = (0, react_1.useState)([]);
    const [isLoadingFilterData, setIsLoadingFilterData] = (0, react_1.useState)(true);
    const clubRepo = (0, react_1.useMemo)(() => (0, ApiClubRepo_1.createApiClubRepo)(), []);
    const machineRepo = (0, react_1.useMemo)(() => (0, ApiMachineRepo_1.createApiMachineRepo)(), []);
    (0, react_1.useEffect)(() => {
        const fetchFilterData = async () => {
            setIsLoadingFilterData(true);
            try {
                const [clubList, machineList] = await Promise.all([
                    (0, getAll_1.getAll)(clubRepo),
                    (0, getBasicInfo_1.getBasicInfo)(machineRepo),
                ]);
                if (clubList) {
                    setClubs(clubList.map((club) => ({
                        label: club.clubName,
                        value: club.clubName,
                        id: club.clubId,
                    })));
                }
                if (machineList) {
                    setMachines(machineList);
                }
            }
            catch (error) {
                console.error('Error fetching filter data:', error);
            }
            finally {
                setIsLoadingFilterData(false);
            }
        };
        fetchFilterData();
    }, [clubRepo, machineRepo]);
    // --- state management (generic, delegated) ---
    const { filters, activeFilterCount, handleFilterChange, handleRemoveFilter: baseHandleRemoveFilter, handleClearAll, showMobileSheet, setShowMobileSheet, } = (0, useTableFilters_1.useTableFilters)({
        storageKey,
        filterKeys: FILTER_KEYS,
        onFilterChange,
    });
    // --- club options (stable) ---
    const clubOptions = (0, react_1.useMemo)(() => clubs.map((club) => ({ label: club.label, value: club.id })), [clubs]);
    // --- helper: compute machine options filtered by club selection ---
    const getMachineOptions = (0, react_1.useCallback)((selectedClubs) => {
        const filtered = selectedClubs.length === 0
            ? machines
            : machines.filter((m) => selectedClubs.includes(m.clubId));
        return filtered.map((m) => ({
            label: m.machineName,
            value: m.micronMachineId,
        }));
    }, [machines]);
    // --- dynamic filter fields: machine options depend on current selections ---
    const getFilterFields = (0, react_1.useCallback)((currentValues) => [
        { key: 'clubs', label: t('club'), options: clubOptions },
        {
            key: 'machines',
            label: t('machine'),
            options: getMachineOptions(currentValues.clubs || []),
        },
    ], [clubOptions, getMachineOptions, t]);
    // --- static filter fields based on committed filters (for chip label resolution) ---
    const filterFields = (0, react_1.useMemo)(() => getFilterFields(filters), [getFilterFields, filters]);
    // --- options map for chip label lookup (all machines, not filtered) ---
    const optionsMap = (0, react_1.useMemo)(() => ({
        clubs: clubOptions,
        machines: machines.map((m) => ({
            label: m.machineName,
            value: m.micronMachineId,
        })),
    }), [clubOptions, machines]);
    // --- wrapped handleFilterChange: auto-remove invalid machines when clubs change ---
    const handleTableFilterChange = (0, react_1.useCallback)((newFilters) => {
        const selectedClubIds = newFilters.clubs || [];
        const selectedMachineIds = newFilters.machines || [];
        if (selectedClubIds.length > 0 && selectedMachineIds.length > 0) {
            const validMachineIds = selectedMachineIds.filter((machineId) => {
                const machine = machines.find((m) => m.micronMachineId === machineId);
                return machine && selectedClubIds.includes(machine.clubId);
            });
            handleFilterChange({ ...newFilters, machines: validMachineIds });
        }
        else {
            handleFilterChange(newFilters);
        }
    }, [machines, handleFilterChange]);
    // --- display filters: hide clubs when machines are selected ---
    const displayFilters = (0, react_1.useMemo)(() => {
        const selectedMachines = filters.machines || [];
        if (selectedMachines.length > 0) {
            return { machines: selectedMachines };
        }
        return filters;
    }, [filters]);
    // --- display active filter count (matches visible chips) ---
    const displayActiveFilterCount = (0, react_1.useMemo)(() => {
        return Object.values(displayFilters).reduce((acc, arr) => acc + (arr?.length || 0), 0);
    }, [displayFilters]);
    return {
        tableFilters: filters,
        displayFilters,
        filterFields,
        getFilterFields,
        optionsMap,
        handleTableFilterChange,
        handleRemoveFilter: baseHandleRemoveFilter,
        handleClearAllFilters: handleClearAll,
        activeFilterCount,
        displayActiveFilterCount,
        isLoadingFilterData,
        showMobileFilters: showMobileSheet,
        setShowMobileFilters: setShowMobileSheet,
    };
}
